/**
 * QuoteRichTextField — reusable contentEditable rich-text field for the
 * three Quote "Notes & Terms" inputs. Mirrors the proposal editor's toolbar
 * (B/I/U, lists, heading, clear) and adds open font-size + font-family
 * controls. Word/Docs paste is cleaned via cleanPaste(). Emits sanitized-ish
 * HTML; the server re-sanitizes on save.
 */
import { useEffect, useRef, useState } from "react";
import { cleanPaste } from "@/lib/proposalCleanPaste";

// Numeric point sizes (8–14), default 12. Replaces the old named-size
// dropdown (Small…Huge). Applied as <span style="font-size:Npt"> so it
// survives the server sanitizer (span+font-size are allow-listed) and
// flows through to the PDF via quote_pdf's _font_open().
const SIZE_MIN = 8;
const SIZE_MAX = 14;
const SIZE_DEFAULT = 12;
const SIZE_OPTIONS = Array.from({ length: SIZE_MAX - SIZE_MIN + 1 }, (_, i) => SIZE_MIN + i);
// Legacy named-size (HTML font size 1–7) → nearest pt in the 8–14 scale.
const OLD_SIZE_TO_PT = { 1: 8, 2: 10, 3: 12, 4: 14, 5: 14, 6: 14, 7: 14 };
const FONT_FAMILIES = ["Default", "Arial", "Georgia", "Times New Roman", "Courier New", "Verdana", "Trebuchet MS"];

// Convert any legacy <font size="N"> in stored HTML to a <span style="font-size:Npt">
// so old quotes migrate to the numeric scale the moment they're edited.
function normalizeStoredSizes(html) {
  if (!html || typeof document === "undefined") return html || "";
  if (!/<font[^>]*\ssize=/i.test(html)) return html;
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  tmp.querySelectorAll("font[size]").forEach((f) => {
    const n = parseInt(f.getAttribute("size"), 10);
    const pt = OLD_SIZE_TO_PT[n] || SIZE_DEFAULT;
    const span = document.createElement("span");
    span.style.fontSize = `${pt}pt`;
    const face = f.getAttribute("face");
    if (face) span.style.fontFamily = face;
    span.innerHTML = f.innerHTML;
    f.replaceWith(span);
  });
  return tmp.innerHTML;
}

const FIELD_CSS = `
  .qrt-wrap { border: 1px solid var(--qb-line, #e2e8f0); border-radius: 8px; background: #fff; }
  .qrt-bar { display: flex; gap: 4px; padding: 6px 7px; flex-wrap: wrap; border-bottom: 1px solid var(--qb-hair, #eef2f7); align-items: center; }
  .qrt-bar button { border: 1px solid var(--qb-line, #e2e8f0); background: #fff; border-radius: 6px;
    min-width: 26px; height: 24px; color: var(--qb-muted, #64748b); font-size: 11.5px; font-weight: 700;
    padding: 0 6px; cursor: pointer; display: inline-flex; align-items: center; justify-content: center;
    transition: border-color 120ms ease, color 120ms ease; }
  .qrt-bar button:hover { border-color: #9dbcfb; color: var(--blue, #2563eb); }
  .qrt-bar select { border: 1px solid var(--qb-line, #e2e8f0); background: #fff; border-radius: 6px;
    height: 24px; font-size: 11px; color: var(--qb-muted, #64748b); padding: 0 4px; cursor: pointer; }
  .qrt-size { display: inline-flex; align-items: center; gap: 2px; }
  .qrt-size .qrt-step { min-width: 20px; width: 20px; }
  .qrt-size input.qrt-size-num { width: 34px; height: 24px; text-align: center; border: 1px solid var(--qb-line, #e2e8f0);
    border-radius: 6px; font-size: 11.5px; font-weight: 700; color: var(--qb-ink-2, #334155); padding: 0; -moz-appearance: textfield; }
  .qrt-size input.qrt-size-num::-webkit-outer-spin-button,
  .qrt-size input.qrt-size-num::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
  .qrt-sep { width: 1px; background: var(--qb-hair, #eef2f7); margin: 2px 3px; height: 18px; align-self: center; }
  .qrt-area { padding: 9px 11px; font-size: 12.5px; line-height: 1.6; color: var(--qb-ink-2, #334155);
    outline: none; min-height: var(--qrt-min, 90px); }
  .qrt-area:empty::before { content: attr(data-ph); color: var(--qb-faint, #94a3b8); font-style: italic; }
  .qrt-area p { margin: 0 0 8px; }
  .qrt-area ul { list-style: disc outside; margin: 0 0 8px; padding-left: 22px; }
  .qrt-area ol { list-style: decimal outside; margin: 0 0 8px; padding-left: 22px; }
  .qrt-area li { display: list-item; margin-bottom: 3px; }
  .qrt-area h1, .qrt-area h2, .qrt-area h3 { margin: 10px 0 6px; color: var(--qb-navy, #0f2a4a); font-weight: 650; line-height: 1.3; }
  .qrt-area h3 { font-size: 1.08em; }
`;

export function QuoteRichTextField({ value, onChange, placeholder, testid, minHeight = 90, accent }) {
  const ref = useRef(null);
  const [size, setSize] = useState(SIZE_DEFAULT);

  useEffect(() => {
    const normalized = normalizeStoredSizes(value || "");
    if (ref.current && ref.current.innerHTML !== normalized) {
      ref.current.innerHTML = normalized;
    }
  }, [value]);

  const exec = (cmd, arg) => {
    ref.current?.focus();
    document.execCommand(cmd, false, arg);
    onChange(ref.current?.innerHTML || "");
  };

  // Apply a numeric point size to the current selection by wrapping it in
  // <span style="font-size:Npt">. execCommand('fontSize', 7) gives us a
  // temporary <font size="7"> wrapper we then convert to the real span.
  const applySize = (pt) => {
    const v = Math.max(SIZE_MIN, Math.min(SIZE_MAX, Number(pt) || SIZE_DEFAULT));
    setSize(v);
    ref.current?.focus();
    try { document.execCommand("styleWithCSS", false, false); } catch (_) { /* noop */ }
    document.execCommand("fontSize", false, "7");
    ref.current?.querySelectorAll('font[size="7"]').forEach((f) => {
      const span = document.createElement("span");
      span.style.fontSize = `${v}pt`;
      span.innerHTML = f.innerHTML;
      f.replaceWith(span);
    });
    onChange(ref.current?.innerHTML || "");
  };

  // Heading toggles H3 ↔ body text (matches the proposal editor).
  const toggleHeading = () => {
    ref.current?.focus();
    let cur = "";
    try { cur = (document.queryCommandValue("formatBlock") || "").toLowerCase(); } catch (_) { /* noop */ }
    exec("formatBlock", cur === "h3" ? "P" : "H3");
  };

  const onPaste = (e) => {
    e.preventDefault();
    const dt = e.clipboardData;
    const htmlSrc = dt.getData("text/html");
    const textSrc = dt.getData("text/plain");
    const cleaned = htmlSrc
      ? cleanPaste(htmlSrc)
      : (textSrc || "").split(/\n{2,}/).map((p) => `<p>${p.replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c])).replace(/\n/g, "<br/>")}</p>`).join("");
    document.execCommand("insertHTML", false, cleaned);
    onChange(ref.current?.innerHTML || "");
  };

  return (
    <div className="qrt-wrap" style={accent ? { borderColor: "var(--amber-light)" } : undefined}>
      <style>{FIELD_CSS}</style>
      <div className="qrt-bar" data-testid={`${testid}-toolbar`}>
        <button type="button" title="Bold" onClick={() => exec("bold")} data-testid={`${testid}-bold`}><b>B</b></button>
        <button type="button" title="Italic" onClick={() => exec("italic")} data-testid={`${testid}-italic`}><i>I</i></button>
        <button type="button" title="Underline" onClick={() => exec("underline")} data-testid={`${testid}-underline`}><u>U</u></button>
        <span className="qrt-sep" />
        <span className="qrt-size" title={`Font size (${SIZE_MIN}–${SIZE_MAX} pt)`}>
          <button type="button" className="qrt-step" title="Smaller" onClick={() => applySize(size - 1)} data-testid={`${testid}-size-dec`}>−</button>
          <input
            type="number"
            className="qrt-size-num"
            min={SIZE_MIN}
            max={SIZE_MAX}
            value={size}
            data-testid={`${testid}-size-input`}
            onChange={(e) => setSize(e.target.value)}
            onBlur={(e) => applySize(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applySize(e.target.value); } }}
          />
          <button type="button" className="qrt-step" title="Larger" onClick={() => applySize(size + 1)} data-testid={`${testid}-size-inc`}>+</button>
          <select title="Pick font size" data-testid={`${testid}-size`} value="" onChange={(e) => { if (e.target.value) applySize(e.target.value); }}>
            <option value="">pt</option>
            {SIZE_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </span>
        <select title="Font family" data-testid={`${testid}-font`} defaultValue="" onChange={(e) => { if (e.target.value) exec("fontName", e.target.value); e.target.value = ""; }}>
          <option value="">Font</option>
          {FONT_FAMILIES.map((f) => <option key={f} value={f === "Default" ? "sans-serif" : f}>{f}</option>)}
        </select>
        <span className="qrt-sep" />
        <button type="button" title="Bulleted list" onClick={() => exec("insertUnorderedList")} data-testid={`${testid}-ul`}>••</button>
        <button type="button" title="Numbered list" onClick={() => exec("insertOrderedList")} data-testid={`${testid}-ol`}>1.</button>
        <button type="button" title="Heading" onClick={toggleHeading} data-testid={`${testid}-h`}>H</button>
        <span className="qrt-sep" />
        <button type="button" title="Clear formatting" onClick={() => exec("removeFormat")} data-testid={`${testid}-clear`}>✕</button>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={() => onChange(ref.current?.innerHTML || "")}
        onPaste={onPaste}
        data-testid={testid}
        data-ph={placeholder || "Type here, or paste from Word — formatting is preserved."}
        className="qrt-area"
        style={{ "--qrt-min": `${minHeight}px` }}
      />
    </div>
  );
}

/**
 * Render stored quote rich-text safely. HTML content → dangerouslySetInnerHTML;
 * legacy plain text → preserved with pre-wrap. Returns null for empty.
 * Injects scoped CSS so list markers + headings survive global list resets.
 */
const QRT_RENDER_CSS = `
  .qrt-render ul { list-style: disc outside; margin: 0 0 8px; padding-left: 22px; }
  .qrt-render ol { list-style: decimal outside; margin: 0 0 8px; padding-left: 22px; }
  .qrt-render li { display: list-item; margin-bottom: 3px; }
  .qrt-render h1, .qrt-render h2, .qrt-render h3 { margin: 10px 0 6px; font-weight: 650; line-height: 1.3; }
  .qrt-render h3 { font-size: 1.1em; }
  .qrt-render p { margin: 0 0 8px; }
`;

export function renderQuoteRichText(value, style) {
  const raw = value || "";
  if (!raw.trim()) return null;
  const looksHtml = /<[a-z][\s\S]*>/i.test(raw);
  // Plain text may still carry entities (&nbsp;, &amp;) from the editor/sanitizer — decode them as HTML.
  const hasEntities = /&(?:[a-z]+|#\d+|#x[0-9a-f]+);/i.test(raw);
  return (
    <div className="qrt-render" style={style}>
      <style>{QRT_RENDER_CSS}</style>
      {looksHtml
        ? <div dangerouslySetInnerHTML={{ __html: raw }} />
        : hasEntities
          ? <div style={{ whiteSpace: "pre-wrap" }} dangerouslySetInnerHTML={{ __html: raw.replace(/</g, "&lt;") }} />
          : <div style={{ whiteSpace: "pre-wrap" }}>{raw}</div>}
    </div>
  );
}

export default QuoteRichTextField;
